import {
    Box, Dialog, Divider, Grid, IconButton, InputAdornment,
    TextField, Typography
} from "@mui/material"
import { CloseOutlined, EmailOutlined, HomeWorkOutlined, KeyOutlined, PaymentsOutlined, PersonOutlined, PhoneOutlined } from "@mui/icons-material"
import { FC, useState } from "react"
import { LoadingButton } from "@mui/lab"
import { isValidPhoneNumber } from "libphonenumber-js"
import { Apis, normalizePhoneNumber } from "../../../helpers"
import { EventsEmit } from "../../../configs/events"
import { PriorTenantProps } from "../../../types/tenants"
import { PropertyProps } from "../../../types/properties"
import { theme } from "../../../theme"
import FindUserDialog from "./findUserDialog"

const PRIMARY = theme.lightTheme.palette.primary.main

interface FieldDef {
    label: string
    field: keyof PriorTenantProps
    numeric?: boolean
    icon: JSX.Element
}

const sections: { title: string, fields: FieldDef[] }[] = [
    {
        title: 'Personal Info', fields: [
            { label: 'Name', field: 'name', icon: <PersonOutlined sx={{ fontSize: 18 }} /> },
            { label: 'Email', field: 'email', icon: <EmailOutlined sx={{ fontSize: 18 }} /> },
            { label: 'Phone', field: 'phone', icon: <PhoneOutlined sx={{ fontSize: 18 }} /> },
        ]
    },
    {
        title: 'Unit Info', fields: [
            { label: 'Unit', field: 'unit', icon: <HomeWorkOutlined sx={{ fontSize: 18 }} /> },
            { label: 'House Number', field: 'unitLabel', icon: <KeyOutlined sx={{ fontSize: 18 }} /> },
        ]
    },
    {
        title: 'Financial Info', fields: [
            { label: 'Deposit (Ksh)', field: 'deposit', numeric: true, icon: <PaymentsOutlined sx={{ fontSize: 18 }} /> },
            { label: 'Deposit Paid (Ksh)', field: 'paidDeposit', numeric: true, icon: <PaymentsOutlined sx={{ fontSize: 18 }} /> },
            { label: 'Price Per Month (Ksh)', field: 'pricePerMonth', numeric: true, icon: <PaymentsOutlined sx={{ fontSize: 18 }} /> },
            { label: 'Arrears (Ksh)', field: 'arrears', numeric: true, icon: <PaymentsOutlined sx={{ fontSize: 18 }} /> },
            { label: 'Balance (Ksh, extra paid by tenant)', field: 'balance', numeric: true, icon: <PaymentsOutlined sx={{ fontSize: 18 }} /> },
        ]
    },
]

const compulsoryFields = ['name', 'phone', 'unit', 'unitLabel', 'deposit']
const zeroValueFields = ['balance', 'arrears']
const numberFields = ['deposit', 'paidDeposit', 'pricePerMonth', 'balance', 'arrears']

interface Props {
    record?: PriorTenantProps | null
    standalone?: boolean
    property: PropertyProps
    close: () => void
    onSaved?: () => void
    onLocalUpdate?: (data: PriorTenantProps) => void
}

interface StateProps {
    values: Record<string, string>
    errors: Record<string, string | null>
    saving: boolean
    showFindUser: boolean
}

const emptyValues = (record?: PriorTenantProps | null): Record<string, string> => ({
    name: record?.name || '',
    email: record?.email || '',
    phone: record?.phone ? record.phone.replace(/^\+254/, '0') : '',
    unit: record?.unit || '',
    unitLabel: record?.unitLabel || '',
    deposit: record?.deposit ? String(record.deposit) : '',
    paidDeposit: record?.paidDeposit ? String(record.paidDeposit) : '',
    pricePerMonth: record?.pricePerMonth ? String(record.pricePerMonth) : '',
    arrears: record?.arrears ? String(record.arrears) : '',
    balance: record?.balance ? String(record.balance) : '',
})

const RecordDialog: FC<Props> = ({ record, standalone, property, close, onSaved, onLocalUpdate }) => {
    const [state, setState] = useState<StateProps>({
        values: emptyValues(record),
        errors: {},
        saving: false,
        showFindUser: false
    })
    const { values, errors, saving, showFindUser } = state
    const isNew = !record?._id

    const changeValue = (field: string, value: string) =>
        setState(s => ({ ...s, values: { ...s.values, [field]: value }, errors: { ...s.errors, [field]: null } }))

    const applyMatchedUser = (user: any) => {
        const fullName = (user.firstName + ' ' + (user.lastName || '')).trim()
        setState(s => ({
            ...s,
            showFindUser: false,
            values: {
                ...s.values,
                name: fullName,
                email: user.email || s.values.email,
                phone: user.phone ? user.phone.replace(/^\+254/, '0') : s.values.phone
            },
            errors: { ...s.errors, name: null, email: null, phone: null }
        }))
    }

    const save = () => {
        const newErrors: Record<string, string | null> = {}
        const obj: any = { propertyId: property._id }
        let hasError = false

        Object.keys(values).forEach(field => {
            let value: any = values[field]
            if (numberFields.includes(field)) value = parseFloat(value) || 0
            else if (field === 'phone') value = normalizePhoneNumber(value)
            else if (field === 'email') value = value?.trim()?.toLowerCase()
            obj[field] = value

            if (!value && compulsoryFields.includes(field) && !zeroValueFields.includes(field)) {
                newErrors[field] = 'Please fill this field.'
                hasError = true
            } else if (field === 'phone' && value && !isValidPhoneNumber(value)) {
                newErrors[field] = 'Please enter a valid phone number.'
                hasError = true
            } else if (field === 'email' && value && !/^\S+@\S+\.\S+$/.test(value)) {
                newErrors[field] = 'Please enter a valid email.'
                hasError = true
            }
        })

        if (hasError) {
            setState(s => ({ ...s, errors: newErrors }))
            EventsEmit('app', { info: { message: 'Please fix the errors above.', status: 'error' } })
            return
        }

        if (record?._id) {
            setState(s => ({ ...s, saving: true }))
            Apis({ module: 'Tenants', action: 'updatePriorTenant', _id: record._id, data: obj })
                .then(({ message }: any) => {
                    EventsEmit('app', { info: { message, status: 'success' } })
                    if (onSaved) onSaved()
                    close()
                })
                .catch(() => setState(s => ({ ...s, saving: false })))
        } else if (standalone) {
            setState(s => ({ ...s, saving: true }))
            Apis({
                module: 'Tenants',
                action: 'addPriorTenants',
                newTenants: [obj],
                property: { name: property.name, logo: property.logos?.small?.url, isExternalPayment: property.isExternalPayment }
            })
                .then(() => {
                    EventsEmit('app', { info: { message: 'Tenant information has been added.', status: 'success' } })
                    if (onSaved) onSaved()
                    close()
                })
                .catch(() => setState(s => ({ ...s, saving: false })))
        } else {
            if (onLocalUpdate) onLocalUpdate(obj)
            EventsEmit('app', { info: { message: 'Tenant information has been updated.', status: 'success' } })
            close()
        }
    }

    return (
        <Dialog open onClose={close} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <Box sx={{ p: 3, maxHeight: '85vh', overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800 }}>
                        {isNew ? 'New' : 'Update'} Tenant Data
                    </Typography>
                    <IconButton size="small" onClick={close}>
                        <CloseOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                {sections.map(({ title, fields }) => (
                    <Box key={title} sx={{ mt: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: .5 }}>
                                {title}
                            </Typography>
                            {title === 'Personal Info' && (
                                <Typography
                                    onClick={() => setState(s => ({ ...s, showFindUser: true }))}
                                    sx={{ fontSize: 11.5, color: PRIMARY, fontWeight: 700, cursor: 'pointer' }}
                                >
                                    Find Registered User
                                </Typography>
                            )}
                        </Box>
                        <Divider sx={{ mt: 0.5, mb: 1.5 }} />
                        <Grid container spacing={2}>
                            {fields.map(({ label, field, icon }) => (
                                <Grid item xs={12} sm={field === 'unitLabel' || field === 'unit' ? 6 : 12} key={field}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={label}
                                        type={field === 'email' ? 'email' : 'text'}
                                        value={values[field]}
                                        onChange={e => changeValue(field, e.target.value)}
                                        error={Boolean(errors[field])}
                                        helperText={errors[field]}
                                        InputProps={{ startAdornment: <InputAdornment position="start">{icon}</InputAdornment> }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                ))}

                <LoadingButton
                    fullWidth
                    variant="contained"
                    loading={saving}
                    onClick={save}
                    sx={{ mt: 3, textTransform: 'none', borderRadius: 2, py: 1.2 }}
                >
                    {isNew ? 'Add' : 'Update'}
                </LoadingButton>
            </Box>

            {showFindUser && (
                <FindUserDialog
                    propertyId={property._id}
                    onSelect={applyMatchedUser}
                    close={() => setState(s => ({ ...s, showFindUser: false }))}
                />
            )}
        </Dialog>
    )
}

export default RecordDialog
